import React, { useState } from "react";
import {
  Container,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  TextField,
  IconButton,
  Snackbar,
} from "@mui/material";

import { ShoppingCart, Add, Remove, Delete, Search } from "@mui/icons-material";

import { useOrders } from "../contexts/OrderContext";
import { apiService } from "../services/api";
import { useQuery } from "@tanstack/react-query";
import { Product } from "../types";

interface Customer {
  id: string;
  full_name: string;
  company_name?: string;
  tax_number?: string;
  address: string;
  phone?: string;
  email?: string;
  contact_person?: string;
  customer_code: string;
  is_active: boolean;
}

interface CartItem {
  product: Product;
  quantity: number;
}

const Sales: React.FC = () => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const { createOrder, isLoading } = useOrders();

  // Fetch products with React Query
  const {
    data: productsData,
    isLoading: productsLoading,
    error: productsError,
  } = useQuery({
    queryKey: ["products", "sales", searchTerm, categoryFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (categoryFilter) params.append("category", categoryFilter);
      params.append("status", "active"); // Only active products
      params.append("limit", "50"); // Limit for sales page

      const response = await apiService.get<{
        data: Product[];
        pagination: any;
      }>(`/products?${params.toString()}`);
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch customers with React Query
  const {
    data: customersData,
  } = useQuery({
    queryKey: ["customers", "sales"],
    queryFn: async () => {
      const response = await apiService.get<{ customers: Customer[] }>(
        "/customers"
      );
      return response;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const products = productsData?.data || [];
  const customers = customersData?.data?.customers || [];

  const addToCart = (product: Product) => {
    const existingItem = cart.find((item) => item.product._id === product._id);
    if (existingItem) {
      setCart(
        cart.map((item) =>
          item.product._id === product._id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCart([...cart, { product, quantity: 1 }]);
    }
  };

  const removeFromCart = (productId: string) => {
    setCart(cart.filter((item) => item.product._id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(
        cart.map((item) =>
          item.product._id === productId ? { ...item, quantity } : item
        )
      );
    }
  };

  const getTotalPrice = () => {
    return cart.reduce(
      (total, item) => total + item.product.selling_price * item.quantity,
      0
    );
  };

  const handleSubmit = async () => {
    if (!selectedCustomer || cart.length === 0) {
      setErrorMessage("Lütfen müşteri seçin ve sepete ürün ekleyin");
      setShowErrorAlert(true);
      return;
    }

    try {
      const orderData = {
        customer_name: selectedCustomer.full_name,
        customer_phone: selectedCustomer.phone,
        customer_address: selectedCustomer.address,
        customer_email: selectedCustomer.email,
        total_amount: getTotalPrice(),
        items: cart.map((item) => ({
          product_name: item.product.name,
          quantity: item.quantity,
          unit_price: item.product.selling_price,
          total_price: item.product.selling_price * item.quantity,
        })),
        notes: `Satış siparişi - Müşteri: ${selectedCustomer.full_name}`,
      };

      await createOrder(orderData);
      setCart([]);
      setSelectedCustomer(null);
      setShowSuccessAlert(true);
      setCartOpen(false);
    } catch (error: any) {
      setErrorMessage(error.message || "Sipariş oluşturulurken hata oluştu");
      setShowErrorAlert(true);
    }
  };

  const handleCloseSuccessAlert = () => {
    setShowSuccessAlert(false);
  };

  const handleCloseErrorAlert = () => {
    setShowErrorAlert(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
    }).format(amount);
  };

  return (
    <Container maxWidth="xl">
      {/* Success/Error Alerts */}
      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={6000}
        onClose={handleCloseSuccessAlert}
      >
        <Alert onClose={handleCloseSuccessAlert} severity="success">
          Sipariş başarıyla oluşturuldu!
        </Alert>
      </Snackbar>

      <Snackbar
        open={showErrorAlert}
        autoHideDuration={6000}
        onClose={handleCloseErrorAlert}
      >
        <Alert onClose={handleCloseErrorAlert} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Satış
        </Typography>
        <Button
          variant="contained"
          startIcon={<ShoppingCart />}
          onClick={() => setCartOpen(true)}
          disabled={cart.length === 0}
        >
          Sepet ({cart.length})
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Ürün Ara"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Kategori</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label="Kategori"
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="Elektronik">Elektronik</MenuItem>
                  <MenuItem value="Bilgisayar">Bilgisayar</MenuItem>
                  <MenuItem value="Giyim">Giyim</MenuItem>
                  <MenuItem value="Ev & Yaşam">Ev & Yaşam</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Müşteri Seç</InputLabel>
                <Select
                  value={selectedCustomer?.id || ""}
                  onChange={(e) => {
                    const customer = customers.find(
                      (c) => c.id === e.target.value
                    );
                    setSelectedCustomer(customer || null);
                  }}
                  label="Müşteri Seç"
                >
                  <MenuItem value="">
                    <em>Müşteri seçin</em>
                  </MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.full_name}{" "}
                      {customer.company_name && `(${customer.company_name})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Products Grid */}
      {productsLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : productsError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Ürünler yüklenirken hata oluştu
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {(products as Product[]).map((product) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <img
                      src={
                        product.images?.[0]?.url ||
                        "https://via.placeholder.com/60x60?text=No+Image"
                      }
                      alt={product.name}
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                    />
                    <Box>
                      <Typography variant="h6" component="h2">
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {product.product_code}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="h6" color="primary" gutterBottom>
                    {formatCurrency(product.selling_price)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Stok: {product.current_stock} {product.unit}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {product.category} • {product.brand}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={() => addToCart(product)}
                    disabled={product.current_stock <= 0}
                    fullWidth
                  >
                    Sepete Ekle
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Cart Dialog */}
      <Dialog
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Sepet</Typography>
            <Typography variant="h6" color="primary">
              Toplam: {formatCurrency(getTotalPrice())}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {cart.length === 0 ? (
            <Typography>Sepetiniz boş</Typography>
          ) : (
            <List>
              {cart.map((item) => (
                <React.Fragment key={item.product._id}>
                  <ListItem>
                    <Box display="flex" alignItems="center" gap={2} flex={1}>
                      <img
                        src={
                          item.product.images?.[0]?.url ||
                          "https://via.placeholder.com/40x40?text=No+Image"
                        }
                        alt={item.product.name}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: 4,
                        }}
                      />
                      <Box flex={1}>
                        <Typography variant="body1">
                          {item.product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(item.product.selling_price)} x{" "}
                          {item.quantity}
                        </Typography>
                      </Box>
                      <Typography variant="h6">
                        {formatCurrency(
                          item.product.selling_price * item.quantity
                        )}
                      </Typography>
                    </Box>
                    <ListItemSecondaryAction>
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            updateQuantity(item.product._id, item.quantity - 1)
                          }
                        >
                          <Remove />
                        </IconButton>
                        <Typography
                          variant="body2"
                          sx={{ minWidth: 20, textAlign: "center" }}
                        >
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() =>
                            updateQuantity(item.product._id, item.quantity + 1)
                          }
                          disabled={item.quantity >= item.product.current_stock}
                        >
                          <Add />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeFromCart(item.product._id)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCartOpen(false)}>İptal</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={cart.length === 0 || !selectedCustomer || isLoading}
          >
            {isLoading ? "Oluşturuluyor..." : "Sipariş Oluştur"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Sales;
